function deriveFeature(url: string, method: string): string {
  if (url.includes("/clients") && method.toUpperCase() === "POST") return "clients";
  if (url.includes("/summary/generate")) return "ai_summary";
  if (url.includes("/export.csv")) return "csv_export";
  if (url.includes("/income-statement")) return "income_statement";
  if (url.includes("/tax-summary")) return "tax_summary";
  if (url.includes("/portal/clients") && url.includes("/trends")) return "weekly_trends";
  if (url.includes("/care-history")) return "care_history";
  return "pro";
}

// `suppressUpgradeModal` lets a screen handle a 402 inline (e.g. render a
// locked upsell card) instead of the global full-screen upgrade modal firing on
// navigation. The 402 still throws so the caller can branch on it; it just
// doesn't dispatch the app-wide `carelogic:upgrade-required` event.
type ApiOptions = RequestInit & { suppressUpgradeModal?: boolean };

export async function api(path: string, options?: ApiOptions) {
  const { suppressUpgradeModal, ...fetchOptions } = options ?? {};
  const doFetch = () => fetch(path, {
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    ...fetchOptions,
  });
  const method = (fetchOptions.method ?? "GET").toUpperCase();
  let res = await doFetch();
  // Transient rate-limit: wait (respecting Retry-After) and retry once before
  // surfacing the error, so a brief 429 doesn't break a normal user action.
  // Reads only — re-sending a mutation after a 429 risks duplicating it if the
  // first attempt was actually processed before the limiter answered.
  if (res.status === 429 && (method === "GET" || method === "HEAD")) {
    const retryAfter = parseInt(res.headers.get("Retry-After") || "", 10);
    const waitMs = Number.isFinite(retryAfter) ? Math.min(retryAfter * 1000, 5000) : 1200;
    await new Promise((r) => setTimeout(r, waitMs));
    res = await doFetch();
  }
  if (res.status === 401) {
    // A single 401 from one endpoint used to hard-redirect the whole app to
    // /login — so a spurious/transient 401 (a brief server blip, or a request
    // in flight across a session-refresh race) bounced an authenticated user
    // to the login screen while the session cookie was still valid (UX
    // refinement 1.6). Confirm with /api/auth/me before redirecting: that probe
    // returns 200 {user: null} when the session is genuinely gone, and 200
    // {user} when it's still valid. Only redirect on a confirmed logout.
    // Never redirect from the probe itself (avoids a loop). Mirrors the P2-1
    // hardening already applied to AuthContext's /me handler.
    if (path === "/api/auth/me") {
      throw new Error("Not authenticated");
    }
    const stillValid = await fetch("/api/auth/me", { credentials: "include" })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => !!d?.user)
      .catch(() => false);
    if (!stillValid) {
      window.location.href = "/login";
    }
    throw Object.assign(new Error("Not authenticated"), { status: 401 });
  }
  if (res.status === 402) {
    const errData = await res.json().catch(() => ({ error: "pro_required" }));
    if (!suppressUpgradeModal) {
      const feature = deriveFeature(path, method);
      window.dispatchEvent(new CustomEvent("carelogic:upgrade-required", {
        detail: { feature }
      }));
    }
    throw Object.assign(new Error(errData.error || "Upgrade required"), { status: 402, data: errData });
  }
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: "Request failed" }));
    throw Object.assign(new Error(err.error || "Request failed"), { status: res.status, data: err });
  }
  // No endpoint returns 204 today, but res.json() on an empty body throws —
  // guard so the first 204 (or empty 200) added to the API doesn't break
  // every caller of api(). (Eng-review 2026-06-09 latent finding.)
  if (res.status === 204) return null;
  return res.json().catch(() => null);
}
